import React from 'react';
import { CloseOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { BaseAlertBanner, BaseButton } from '@components';
import { AbsoluteWrapper as Wrapper } from '@components/alert-banner/styled.tsx';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { useGetClientIdAndTenantAlias } from '@hooks/useGetClientIdAndTenantAlias.ts';
import { useLingui } from '@lingui/react/macro';
import {
    clearVesselsState,
    downloadVesselsExampleCsv,
    setIsUploadCsvModalOpened,
    setUploadedCsvFile,
} from '@routes/sidebar/admin-center/___shared___/model/vessels.slice.ts';
import { downloadByUrl } from '@utils/download.ts';

import { Actions, Description, DescriptionWrapper } from './styled.tsx';

interface Props {
    filename: string;
}

export const CSVImportFailedBanner: React.FC<Props> = ({ filename }) => {
    const { t } = useLingui();
    const dispatch = useAppDispatch();

    const { clientId, tenantAlias, isClientIdAndTenantAliasAvailable } = useGetClientIdAndTenantAlias();

    const { csvUploadState } = useAppSelector((state) => state.adminCenterVessels);

    const handleClose = () => {
        dispatch(clearVesselsState());
    };

    const handleTryDifferentFile = () => {
        dispatch(clearVesselsState());
        dispatch(setUploadedCsvFile(null));
        dispatch(setIsUploadCsvModalOpened(true));
    };

    const handleDownloadCSVTemplate = () => {
        if (!isClientIdAndTenantAliasAvailable) return;
        void dispatch(downloadVesselsExampleCsv({ clientId, tenantAlias }));
    };

    const handleDownloadFileWithErrors = () => {
        if (csvUploadState?.issuesFileName) {
            downloadByUrl(csvUploadState.issuesFileName);
        }
    };

    return (
        <Wrapper $width={'50%'}>
            <BaseAlertBanner
                closable={{
                    closeIcon: <CloseOutlined />,
                    onClose: handleClose,
                }}
                type="error"
                title={`${filename} - import failed`}
                description={
                    <DescriptionWrapper>
                        <Description>
                            <div>{t`The CSV file could not be imported. Please check the following:`}</div>
                            <ul>
                                <li>{t`File format is valid CSV`}</li>
                                <li>{t`File is not corrupted or empty`}</li>
                            </ul>
                        </Description>
                        <Actions>
                            <BaseButton
                                onClick={handleTryDifferentFile}
                                $customType={'outline'}
                            >{t`Try different file`}</BaseButton>
                            <BaseButton
                                onClick={handleDownloadCSVTemplate}
                                $customType={'link'}
                            >{t`Download CSV template`}</BaseButton>
                            {csvUploadState?.issuesFileName !== null && (
                                <BaseButton
                                    onClick={handleDownloadFileWithErrors}
                                    $customType={'link'}
                                >{t`Download file with errors`}</BaseButton>
                            )}
                        </Actions>
                    </DescriptionWrapper>
                }
                icon={<ExclamationCircleFilled />}
            />
        </Wrapper>
    );
};
